import { colors } from '@/assets/styles/colors';

/**
 * Input variant types - determines visual structure
 * - floating: Label floats up when focused/has value
 * - standard: Label above input, simple underline
 */
export type InputVariant = 'floating' | 'standard';

/**
 * Input color presets - determines color scheme
 * Follows same pattern as Selector component
 */
export type InputColor = 'default' | 'important' | 'success' | 'alert' | 'error';

/**
 * Input type for HTML input element
 */
export type InputType = 'text' | 'password' | 'email' | 'number' | 'tel' | 'url' | 'search';

/**
 * Color tokens for input styling
 */
export interface InputColors {
  /** Label text color */
  label: string;
  /** Input text color */
  text: string;
  /** Supporting/helper text color */
  supporting: string;
  /** Border color */
  border: string;
  /** Icon color */
  icon: string;
}

/**
 * Color tokens for each color variant (excluding default)
 */
export const INPUT_COLOR_TOKENS: Record<Exclude<InputColor, 'default'>, string> = {
  important: colors.important.main,
  success: colors.feedback.success300,
  alert: colors.feedback.alert300,
  error: colors.feedback.error300,
};

/**
 * Pure function to resolve input colors based on state
 * 
 * Color behavior:
 * - Default: Label neutral66, border neutrale5 (→ important.main on focus), icon primary.auxiliar
 * - Important/Success/Alert: Label, border (only on focus), and icon use variant color
 * - Error: Always shows error300 for label, border, supporting text, and icon (no focus change)
 * 
 * Text is always neutral33, supporting text is neutral99 (except error → error300)
 */
export function getInputColors(args: {
  color: InputColor;
  disabled: boolean;
  focused: boolean;
  hasValue: boolean;
}): InputColors {
  const { color, disabled, focused, hasValue } = args;

  // Disabled state overrides everything
  if (disabled) {
    return {
      label: colors.grays.neutral99,
      text: colors.grays.neutral99,
      supporting: colors.grays.neutral99,
      border: colors.grays.neutralE5,
      icon: colors.grays.neutral99,
    };
  }

  // Error state - always shows error colors (no focus change)
  if (color === 'error') {
    return {
      label: colors.feedback.error300,
      text: colors.grays.neutral33,
      supporting: colors.feedback.error300,
      border: colors.feedback.error300,
      icon: colors.feedback.error300,
    };
  }

  // Default color variant
  if (color === 'default') {
    return {
      label: colors.grays.neutral66,
      text: colors.grays.neutral33,
      supporting: colors.grays.neutral99,
      border: focused ? colors.important.main : colors.grays.neutralE5,
      icon: colors.primary.auxiliar,
    };
  }

  // Other color variants (important, success, alert)
  const variantColor = INPUT_COLOR_TOKENS[color];
  const isActive = focused || hasValue;

  return {
    label: isActive ? variantColor : colors.grays.neutral66,
    text: colors.grays.neutral33,
    supporting: colors.grays.neutral99,
    border: focused ? variantColor : colors.grays.neutralE5,
    icon: variantColor,
  };
}
